/**
 * Collegamenti-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Collegamenti game events.
 * Collegamenti has three event types:
 * - 'submit': Submit 4 selected words to check if they form a valid group
 * - 'hint_reveal_theme': Reveal the theme of an undiscovered group
 * - 'hint_reveal_elements': Reveal 3 of 4 words from an undiscovered group
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent, dispatchHintEvent } = useGameplaySession('collegamenti', date);
 * const { submitWords, revealTheme, revealElements } = useCollegamentiEvents(dispatchEvent, dispatchHintEvent);
 * 
 * const result = await submitWords(['WORD1', 'WORD2', 'WORD3', 'WORD4']);
 * if (result.success) {
 *   // result.result contains: { valid, reason?, theme?, color?, explanation?, answers? }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Collegamenti-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @param {function} dispatchHintEvent - The dispatchHintEvent function from useGameplaySession
 * @returns {object} Collegamenti event helpers
 * @returns {function} returns.submitWords - Submit 4 selected words to check for a valid group
 * @returns {function} returns.revealTheme - Request to reveal an undiscovered group's theme (hint)
 * @returns {function} returns.revealElements - Request to reveal 3 of 4 elements of an undiscovered group (hint)
 */
export function useCollegamentiEvents(dispatchEvent, dispatchHintEvent) {
  /**
   * Submit 4 selected words to check if they form a valid group.
   * 
   * @param {string[]} words - Array of 4 selected words
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     valid: boolean,
   *     reason?: 'INVALID_GROUP' | 'WORD_NOT_IN_BOARD' | 'ALMOST_CORRECT',
   *     theme?: string,
   *     color?: string,
   *     explanation?: string,
   *     answers?: Array<{theme: string, words: string[], color: string, explanation: string}>
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitWords(['MICHETTA', 'FOCACCIA', 'CIABATTA', 'GRISSINO']);
   * if (result.success) {
   *   if (result.result.valid) {
   *     // Group found! Use result.result.theme, .color, .explanation
   *   } else {
   *     // Wrong guess - check result.result.reason
   *     // state.lives shows remaining lives
   *   }
   * }
   */
  const submitWords = useCallback(async (words) => {
    return dispatchEvent('submit', { words });
  }, [dispatchEvent]);

  /**
   * Request to reveal the theme of an undiscovered group (hint).
   * Requires authentication and sufficient gettoncini balance.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { theme: string },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await revealTheme(() => showSignupModal());
   * if (result.success) {
   *   const theme = result.hint_data.theme;
   *   // Display theme hint to user
   * }
   */
  const revealTheme = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_theme', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request to reveal 3 of 4 elements from an undiscovered group (hint).
   * Requires authentication and sufficient gettoncini balance.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { words: [string, string, string] },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await revealElements(() => showSignupModal());
   * if (result.success) {
   *   const words = result.hint_data.words;
   *   // Auto-select the 3 revealed words
   * }
   */
  const revealElements = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_elements', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  return {
    submitWords,
    revealTheme,
    revealElements,
  };
}

